import { useCallback, useEffect, useState } from 'react';

// Detección de iOS/iPadOS. iPadOS 13+ se anuncia como "MacIntel" en
// navigator.platform (Safari de escritorio "falso"), así que se distingue
// de un Mac real por soportar touch (maxTouchPoints > 1).
function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isClassicIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
  const isIPadOS13Plus = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isClassicIOS || isIPadOS13Plus;
}

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  const mediaStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // navigator.standalone: propiedad no estándar de Safari/iOS, es la única
  // señal confiable ahí (iOS no soporta bien matchMedia display-mode).
  const iosStandalone = window.navigator.standalone === true;
  return Boolean(mediaStandalone || iosStandalone);
}

// Detección por User-Agent, no por dispositivo: un Samsung físico usando
// Chrome (UA sin "SamsungBrowser") debe seguir el flujo Chromium normal, no
// este. Samsung Internet es quien genera el WebAPK que dispara la
// advertencia de Play Protect por targetSdk antiguo — algo que no
// controlamos desde la PWA.
function detectSamsungInternet() {
  if (typeof navigator === 'undefined') return false;
  return /SamsungBrowser/i.test(navigator.userAgent || '');
}

// Hook reutilizable para toda la lógica de instalación PWA. Android/
// Chromium, iOS y Samsung Internet son deliberadamente distintos (ver
// comentarios): no existe un único camino "instalar" que sirva para todos.
export function usePWAInstall() {
  const [isIOS] = useState(detectIOS);
  const [isSamsungInternet] = useState(detectSamsungInternet);
  const [installed, setInstalled] = useState(detectStandalone);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Android/Chromium: el navegador dispara este evento cuando decide que
    // la app ES instalable. Se captura y NUNCA se deja pasar al prompt
    // nativo del navegador sin contexto — se guarda para usarlo solo cuando
    // el usuario toca nuestro propio botón "Instalar aplicación".
    function handleBeforeInstallPrompt(event) {
      event.preventDefault();
      setDeferredPrompt(event);
    }

    function handleAppInstalled() {
      setInstalled(true);
      setDeferredPrompt(null);
    }

    function handleDisplayModeChange() {
      setInstalled(detectStandalone());
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    const mql = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null;
    mql?.addEventListener?.('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mql?.removeEventListener?.('change', handleDisplayModeChange);
    };
  }, []);

  const isAndroidOrChromiumCompatible = Boolean(deferredPrompt);

  // Instalable = hay algo útil que ofrecer: en Chromium, el evento nativo
  // capturado; en iOS, siempre que no esté ya instalada (ahí solo podemos
  // mostrar instrucciones); en Samsung Internet, siempre (aunque no haya
  // evento nativo) porque mostramos información en vez de un instalador.
  const installable = !installed && (isAndroidOrChromiumCompatible || isIOS || isSamsungInternet);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === 'accepted') {
      setInstalled(true);
    }
    return choice;
  }, [deferredPrompt]);

  return {
    installable,
    installed,
    isIOS,
    isSamsungInternet,
    isAndroidOrChromiumCompatible,
    deferredPrompt,
    promptInstall,
  };
}
